import React, {
  useState,
  useEffect,
  useRef,
  CSSProperties,
  ReactNode,
  HTMLAttributes,
} from "react";

export interface CollapseProps extends HTMLAttributes<HTMLDivElement> {
  isOpen: boolean;
  children: ReactNode;
  onEntering?: () => void;
  onEntered?: () => void;
  onExiting?: () => void;
  onExited?: () => void;
  transitionDuration?: number;
  className?: string;
  style?: CSSProperties;
}

// Replacing the Collapse component from the reactstrap package
const Collapse = ({
  isOpen,
  children,
  onEntering,
  onEntered,
  onExiting,
  onExited,
  transitionDuration = 300,
  className = "",
  style,
  ...rest
}: CollapseProps) => {
  const [height, setHeight] = useState<number | string>("auto");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const previousIsOpenRef = useRef(isOpen);
  const firstRenderRef = useRef(true);
  const animationFrame = useRef<number | null>(null);
  const timeout = useRef<number | null>(null);

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      setHeight(isOpen ? "auto" : 0);
      return;
    }

    if (previousIsOpenRef.current === isOpen) return;
    const node = contentRef.current;
    if (!node) return;

    if (timeout.current) clearTimeout(timeout.current);
    if (animationFrame.current) cancelAnimationFrame(animationFrame.current);

    const startTransition = () => {
      setIsTransitioning(true);

      if (isOpen) {
        onEntering?.();
        const startHeight = node.getBoundingClientRect().height;
        node.style.height = "auto";
        const endHeight = node.scrollHeight;
        node.style.height = `${startHeight}px`;

        animationFrame.current = requestAnimationFrame(() => {
          setHeight(endHeight);
        });
      } else {
        onExiting?.();
        const startHeight = node.getBoundingClientRect().height;
        setHeight(startHeight);

        animationFrame.current = requestAnimationFrame(() => {
          setHeight(0);
        });
      }
    };

    timeout.current = window.setTimeout(startTransition, 10);
    previousIsOpenRef.current = isOpen;

    return () => {
      if (timeout.current) clearTimeout(timeout.current);
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    };
  }, [isOpen, onEntering, onExiting]);

  const handleTransitionEnd = (e: React.TransitionEvent) => {
    if (e.target !== contentRef.current || e.propertyName !== "height" || !isTransitioning) return;

    setIsTransitioning(false);

    if (isOpen) {
      setHeight("auto");
      onEntered?.();
    } else {
      onExited?.();
    }
  };

  const collapseStyles: CSSProperties = {
    overflow: "hidden",
    height: height === "auto" ? "auto" : `${height}px`,
    transition: isTransitioning ? `height ${transitionDuration}ms ease-in-out` : "none",
    boxSizing: "content-box",
    ...style,
  };

  return (
    <div
      ref={contentRef}
      style={collapseStyles}
      className={`${className}`}
      onTransitionEnd={handleTransitionEnd}
      aria-expanded={isOpen}
      {...rest}
    >
      {children}
    </div>
  );
};

export default Collapse;
